import {CollisionEventType, Component, ForceMode, LockAxis, Object3D, PhysXComponent} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import Logger from './utils/logger.js';
import { vec3 } from 'gl-matrix';

const MODULE = 'PlayerMovement';
const headPivotWorldBefore = vec3.create();
const headPivotWorldAfter = vec3.create();
const headPivotCorrection = vec3.create();

/**
 * player-movement-handler
 */
export class PlayerMovementHandler extends Component {
    static TypeName = 'player-movement-handler';

    @property.object()
    playerPhysXObject: Object3D | null = null; // The PhysX component that will be moved. Can be the same object as this component is on, or a parent.

    @property.object()
    playerRotationObject: Object3D | null = null; // The object that will be rotated for snap/smooth rotation. Can be the same object as this component is on, or a parent.

    @property.object()
    playerVRHeadObject: Object3D | null = null; // The VR headset object, used to determine forward direction for movement. Should be a child of playerRotationObject if playerRotationObject is set.

    @property.float(6)
    baseJumpForce: number = 6.0;

    @property.float(0.4)
    jumpCooldownSeconds: number = 0.4;    
    
    @property.int(6)
    platformMask: number = 6; // Default to group 6

    _platformMask: number = 1 << this.platformMask

    private jumpRequested: boolean = false;
    private lastJumpTime: number = 0;
    private contactedPlatforms: Array<PhysXComponent> = [];
    private queuedForceVector: vec3 = [0, 0, 0];
    private queuedAdditiveForceVector: vec3 = [0, 0, 0];
    private queuedYawRateDegPerSec: number = 0; // Smooth turn rate, will be multiplied by dt
    private queuedSnapYawDeg: number = 0; // Snap turn delta, applied directly
    private playerPhysXComp: PhysXComponent | null = null;

    private get jumpCooling() : boolean {
        const currentTime = performance.now() / 1000; // Convert to seconds
        const timeSinceLastJump = currentTime - this.lastJumpTime;
        if (timeSinceLastJump < this.jumpCooldownSeconds) {
            return true; 
        }

        return false;
    }

    start() {
        this.playerPhysXComp = this.playerPhysXObject?.getComponent(PhysXComponent) ?? null;
        this.playerPhysXComp?.onCollision(this.onCollision.bind(this));
    }

    onCollision(type: CollisionEventType, other: PhysXComponent) {
        //Logger.debug("Collision detected:" + type + " " + (other.groupsMask & this._platformMask), MODULE);
        switch (type) {
            case CollisionEventType.Touch:
                if (other.groupsMask & this._platformMask) { // platform group
                    this.contactedPlatforms.push(other);
                }
                break;
            case CollisionEventType.TouchLost:
                this.contactedPlatforms = this.contactedPlatforms.filter((comp) => comp !== other);
                //Remove object from tracked list of platforms (destructive)
                break;
        }
    }

    /**
     * Clears all contacted platforms. Call this when a scene switches.
     */
    public clearContactedPlatforms(): void {
        this.contactedPlatforms = [];
    }

    /**
     * Validates and removes destroyed platform components from the contacted platforms list.
     */
    private validateContactedPlatforms(): void {
        this.contactedPlatforms = this.contactedPlatforms.filter((comp) => {
            return comp && !comp.object.isDestroyed;
        });
    }

    private rotatePlayerAroundHeadPivot(yawDeltaDegrees: number): void {
        if (!this.playerRotationObject || !Number.isFinite(yawDeltaDegrees) || yawDeltaDegrees === 0) {
            return;
        }

        if (!this.playerVRHeadObject) {
            this.playerRotationObject.rotateAxisAngleDegObject([0, 1, 0], yawDeltaDegrees);
            return;
        }

        this.playerVRHeadObject.getPositionWorld(headPivotWorldBefore);
        this.playerRotationObject.rotateAxisAngleDegObject([0, 1, 0], yawDeltaDegrees);
        this.playerVRHeadObject.getPositionWorld(headPivotWorldAfter);

        vec3.sub(headPivotCorrection, headPivotWorldBefore, headPivotWorldAfter);
        headPivotCorrection[1] = 0;
        this.playerRotationObject.translateWorld(headPivotCorrection);
    }

    update(dt: number) {
        // Validate and remove any destroyed platform components
        this.validateContactedPlatforms();

        let coolingNow = this.jumpCooling;
        if (!coolingNow) {
            if (this.jumpRequested) {
                this.jump();
            }

            if (this.playerPhysXComp) {
                const combinedForce = vec3.create();
                vec3.add(combinedForce, this.queuedForceVector, this.queuedAdditiveForceVector);
                if (vec3.squaredLength(combinedForce) > 0) {
                    this.playerPhysXComp.addForce(combinedForce);
                }
            }
        }
        if (this.playerPhysXComp) {
			this.playerPhysXComp.angularLockAxis = this.playerPhysXComp.angularLockAxis | LockAxis.Y;
        }

        // Apply snap and smooth rotation in exclusive paths to avoid mixed-mode turning.
        const snapYawDeltaDeg = this.queuedSnapYawDeg;
        if (Number.isFinite(snapYawDeltaDeg) && snapYawDeltaDeg !== 0) {
            this.rotatePlayerAroundHeadPivot(snapYawDeltaDeg);
        } else {
            const smoothYawDeltaDeg = this.queuedYawRateDegPerSec * dt;
            if (Number.isFinite(smoothYawDeltaDeg) && smoothYawDeltaDeg !== 0) {
                this.rotatePlayerAroundHeadPivot(smoothYawDeltaDeg);
            }
        }

        this.jumpRequested = false;
        vec3.zero(this.queuedForceVector);
        vec3.zero(this.queuedAdditiveForceVector);
        this.queuedSnapYawDeg = 0;
        this.queuedYawRateDegPerSec = 0;
    }

    private jump() {
        this.jumpRequested = false; // Reset jump request regardless of successful
        //jump now, because
        if (this.contactedPlatforms.length > 0 && this.playerPhysXComp) {
            const jumpForceVec = vec3.fromValues(0, this.baseJumpForce, 0);
            this.playerPhysXComp.addForce(jumpForceVec, ForceMode.Impulse);
            this.lastJumpTime = performance.now() / 1000;
        }
    }

    public requestJump() {
        this.jumpRequested = true;
    }

    public requestMovement(direction: vec3) {
        this.queuedForceVector = direction;
    }

    public requestMovementAdditive(direction: vec3) {
        if (!direction || direction.length < 3) {
            return;
        }

        this.queuedAdditiveForceVector[0] += direction[0];
        this.queuedAdditiveForceVector[1] += direction[1];
        this.queuedAdditiveForceVector[2] += direction[2];
    }

    public requestYawDeltaDeg(yawRateDegPerSec: number) {
        if (!Number.isFinite(yawRateDegPerSec) || yawRateDegPerSec === 0) {
            return;
        }

        this.queuedYawRateDegPerSec += yawRateDegPerSec;
    }

    public requestTorque(torque: vec3) {
        const yawDelta = torque?.[1] ?? 0;
        this.requestYawDeltaDeg(yawDelta);
    }

    public requestSnapRotate(yawDeltaDegrees: number) {
        if (!Number.isFinite(yawDeltaDegrees) || yawDeltaDegrees === 0) {
            return;
        }
        this.queuedSnapYawDeg += yawDeltaDegrees;
    }
}
